import { SERVICE_TYPE } from '@hedepy/common/enums/SERVICE_TYPE'
import { useRouter } from 'next/router'
import QueryString from 'qs'
import { useEffect, useMemo, useState } from 'react'
import { useEffectEvent } from 'use-effect-event'

import { useSession } from '@/legacy-src/contexts/session-context'
import toaster from '@/legacy-src/utilities/toaster'

import type { CartParams, CartParamSlot } from '@/src/domains/cart/types'
import { hashCartParams } from '@/src/domains/cart/utils/hashCartParams'
import { parseCartParams } from '@/src/domains/cart/utils/parseCartParams'

export interface UseCartState {
  therapistId: number
  serviceId: number
  serviceType?: number
}

interface CartOwner {
  therapistId: number
  serviceId: number
}

type OwnedCartParams = CartParams & CartOwner

const getIsCartOwnedBy = (params: CartParams, { therapistId, serviceId }: CartOwner) =>
  params.therapistId === therapistId && params.serviceId === serviceId

const getHasCartContent = ({ selected, prebookingIds, voucher }: CartParams) =>
  selected.length > 0 || prebookingIds.length > 0 || Boolean(voucher)

const getCartUrl = ({ params, isLoggedIn }: { params: OwnedCartParams; isLoggedIn: boolean }) => {
  const cartUrl = `/client/reservation/cart?${QueryString.stringify({
    therapistId: params.therapistId,
    service: params.serviceId,
    params: hashCartParams(params),
  })}`

  return isLoggedIn ? cartUrl : `/register/reservation?redirectUrl=${encodeURIComponent(cartUrl)}`
}

export const useCartState = ({ therapistId, serviceId, serviceType }: UseCartState) => {
  const router = useRouter()
  const params = useMemo(() => parseCartParams(router.query.params as string), [router.query.params])

  const { isLoggedIn } = useSession()

  const [bypassSubscriptionAndCompanyBenefits, setBypassSubscriptionAndCompanyBenefits] = useState(false)

  const isOwned = getIsCartOwnedBy(params, { therapistId, serviceId })

  const ownedParams = useMemo(
    () =>
      isOwned
        ? { ...params, therapistId, serviceId }
        : { therapistId, serviceId, selected: [], prebookingIds: [], voucher: undefined },
    [isOwned, params, therapistId, serviceId],
  )

  const hasDisownedCart = !isOwned && getHasCartContent(params)

  const resetDisownedCart = useEffectEvent(() => {
    router.replace(
      {
        pathname: router.route,
        query: { ...router.query, params: hashCartParams(ownedParams) },
      },
      undefined,
      { shallow: true },
    )
  })

  useEffect(() => {
    if (!hasDisownedCart) return

    resetDisownedCart()
  }, [hasDisownedCart])

  return useMemo(() => {
    const { selected, prebookingIds, voucher } = ownedParams

    const replaceParams = (nextParams: OwnedCartParams) => {
      router.replace(
        {
          pathname: router.route,
          query: { ...router.query, params: hashCartParams(nextParams) },
        },
        undefined,
        { shallow: true },
      )
    }

    const addSlot = (slot: CartParamSlot, { proceedToCart = false }: { proceedToCart?: boolean } = {}) => {
      if (serviceType === SERVICE_TYPE.FREE_DEMO && selected.length >= 1) {
        toaster.error('component.cart.slotErrors.freeIntroSessionAlreadyInCart')
        return
      }

      const newParams = {
        ...ownedParams,
        selected: [...selected, slot],
      }

      if (proceedToCart) {
        router.push(getCartUrl({ params: newParams, isLoggedIn }))
      } else {
        replaceParams(newParams)
      }
    }

    const removeSlot = (startDate: string) => {
      replaceParams({
        ...ownedParams,
        selected: selected.filter((slot) => slot.startDate !== startDate),
      })
    }

    const removePrebookingId = (id: number) => {
      replaceParams({
        ...ownedParams,
        prebookingIds: prebookingIds.filter((prebookingId) => prebookingId !== id),
      })
    }

    const setVoucher = (nextVoucher?: string) => {
      replaceParams({
        ...ownedParams,
        voucher: nextVoucher,
      })
      if (!nextVoucher) {
        setBypassSubscriptionAndCompanyBenefits(false)
      }
    }

    const cartUrl = getCartUrl({ params: ownedParams, isLoggedIn })

    return {
      selectedSlots: selected.map((slot) => ({ ...slot, therapistId, serviceId })),
      serviceType,
      addSlot,
      removeSlot,
      prebookingIds,
      removePrebookingId,
      voucher,
      setVoucher,
      cartUrl,
      bypassSubscriptionAndCompanyBenefits,
      setBypassSubscriptionAndCompanyBenefits,
    }
  }, [ownedParams, router, therapistId, serviceId, serviceType, isLoggedIn, bypassSubscriptionAndCompanyBenefits])
}
